import type { HiringEventData } from "../hooks/useConditionalHiringEventsData";
import type { ResumeEngagementContextData } from "../hooks/useFindResumeEngagement";
import type { ViewjobOneGraphModel } from "../viewjobOneGraphModel";

import React, { useContext } from "react";

/**
 * Attributes needed to fire legacy auto-open job-click tracking from RNVJ's
 * ApplyButton. Mirrors the `selectedJobAttributes` shape consumed by the legacy
 * web `triggerJobClickTracking` / `triggerRawClickLogging` (jasxJobClick.tsx).
 *
 * Presence is the signal: this is only set for the first auto-opened job in a
 * two-pane desktop feed, so a defined value always means "auto-open" (the legacy
 * `isAutoOpen` flag is therefore implied and not carried separately).
 */
export interface JobClickTrackingSelectedJobAttributes {
  jobKey?: string;
  link?: string;
  extractTrackingUrls?: string[] | null;
  tk?: string;
  adId?: string;
  advn?: string;
}

export type IViewJobDataContext = {
  initialData?: model.viewjob.ViewJobClientSideModel;
  viewjobOneGraphModel?: ViewjobOneGraphModel;
  isDesktopSERP?: boolean;
  trackJobClickOnApply?: boolean;
  jobClickTrackingAttributes?: JobClickTrackingSelectedJobAttributes;
  isSerpShowingCompanyHeader?: boolean;
  domain: string;
  viewjobSourceUrl: string;
  proctorGroups?: string | null;
  resumeEngagementContext?: ResumeEngagementContextData;
  hiringEventsData?: HiringEventData;
  from?: string | null;
  /**
   * The Indeed Apply link URL, loaded asynchronously after the main viewjob data.
   * This is used by the IndeedApplyButton to construct the apply URL.
   * null means the URL hasn't loaded yet or is not applicable.
   */
  applyLinkUrl?: string | null;
  /**
   * Whether the apply link is currently loading.
   */
  applyLinkLoading?: boolean;
};

export const ViewJobDataContext = React.createContext<IViewJobDataContext>({
  initialData: undefined,
  viewjobOneGraphModel: undefined,
  domain: "",
  viewjobSourceUrl: "",
  proctorGroups: "",
  hiringEventsData: undefined,
  applyLinkUrl: null,
  applyLinkLoading: false,
});

export default ViewJobDataContext;

/**
 * a hook for accessing the view job data context without wrapping the component in `ViewJobDataContext.Provider`
 *
 * @returns the value of the view job context provider
 */
export const useViewJobDataContext = (): IViewJobDataContext => useContext(ViewJobDataContext);

export const useViewjobOneGraphModel = () => useContext(ViewJobDataContext).viewjobOneGraphModel;

export const useDomainUrl = () => useContext(ViewJobDataContext).domain;

export const useViewjobSourceUrl = () => useContext(ViewJobDataContext).viewjobSourceUrl;

export const useProctorGroups = () => useContext(ViewJobDataContext).proctorGroups;

export const useResumeEngagementContextData = () =>
  useContext(ViewJobDataContext).resumeEngagementContext;

export const useHiringEventsData = () => useContext(ViewJobDataContext).hiringEventsData;

export const useApplyLinkUrl = () => useContext(ViewJobDataContext).applyLinkUrl;

export const useApplyLinkLoading = () => useContext(ViewJobDataContext).applyLinkLoading;

/**
 * Job-click tracking attributes for the current (auto-opened) job, if any.
 * Used by the ApplyButton to fire legacy auto-open click tracking on apply.
 */
export const useJobClickTrackingAttributes = () =>
  useContext(ViewJobDataContext).jobClickTrackingAttributes;

/**
 * Hook to determine if the current ViewJob display is for desktop (web).
 * Returns true if the host display surface is 'DESKTOP_STANDALONE' or
 * 'DESKTOP_EMBEDDED'. Re-exported here for backward compatibility; the source of
 * truth is the dedicated {@link ViewJobDisplayContext}.
 */
export { useIsDesktopDisplay } from "./ViewJobDisplayContext";
